import React from 'react';
import { Link } from 'react-router';
import { useAtomValue } from 'jotai';
import { Button } from '@archipro/rocco/components/button';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { CarouselSection } from '@rocco/ui/layout/components/CarouselSection';
import { Separator } from '@rocco/components/separator';
import { Skeleton } from '@rocco/components/skeleton';
import { NoMatchesFound } from '@rocco/ui/no-matches/components/NoMatchesFound';
import { SEARCH_CATEGORIES } from '@modules-rocco/search/config/search-service.config';
import { generateURL } from '@modules-rocco/url/api/generate-url';
import type { SearchIndexes } from '@rocco/types/enum/search-indexes';
import { isProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { isArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import type { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { searchPageDataAtom } from '../states/search-page-data-atom';
import type { TileableModel } from '@rocco/ui/tile-shared/view-models/tile'; // Import TileableModel
import type { SearchServiceResult } from '../api/get-search-paginated-results.server'; // Import SearchServiceResult
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { getPinType } from '~/modules/moodboards/utils/tile-pin-type';
import { useCategoryRevamp } from '@modules/category/hook/use-category-revamp';
import { getSearchTileAspect } from '@modules-rocco/search/util/get-search-tile-aspect';
import { cn } from '@rocco/utils/cn';

// The revamp card aspect each vertical's View All box matches, mirroring
// getSearchTileAspect for the group's tiles.
const VIEW_ALL_ASPECT_CLASSES: Record<SearchIndexes, string> = {
    product: 'aspect-tile-portrait',
    professional: 'aspect-tile-portrait',
    project: 'aspect-tile-portrait',
    blogpost: 'aspect-tile-article',
};

// Define SearchResultGroup locally based on its usage
interface SearchResultGroup {
    index: SearchIndexes;
    tiles: TileableModel[]; // Assuming tiles is the processed result, adjust if needed based on actual data shape
    searchResponse?: SearchServiceResult; // Optional, if needed from the original structure
}

type MobileSearchResultsAllProps = ReturnType<typeof useCategoryFilterSort> & {
    isLoading: boolean;
};

/**
 * Renders the "All" results view for mobile, which typically shows a carousel of results for each category.
 * Includes loading states and a "View All" button for each category.
 */
export const MobileSearchResultsAll: React.FC<MobileSearchResultsAllProps> = (
    props,
) => {
    const pageDataFromAtom = useAtomValue(searchPageDataAtom);
    const searchResultGroups = pageDataFromAtom?.searchResultGroups ?? [];
    const appliedSearchQueryForLink = pageDataFromAtom?.searchQuery ?? '';
    const filterStatesFromAtom = pageDataFromAtom?.filterStates;
    const isLoadingFromProps = props.isLoading;
    const { isInDesignBoard } = useHandleFollow();
    const categoryRevamp = useCategoryRevamp();

    const generateViewAllTile = (searchIndexValue: SearchIndexes) => {
        const category = SEARCH_CATEGORIES.find(
            (c) => c.index === searchIndexValue,
        )?.category;

        const urlParams: { search?: string; category?: string } = {};
        if (appliedSearchQueryForLink) {
            urlParams.search = appliedSearchQueryForLink;
        }
        if (category) {
            urlParams.category = category;
        }

        return (
            <div
                className={cn(
                    'flex items-center justify-center rounded-tile bg-gray-100',
                    // Match the group's tile shape — it sits in the same row as
                    // the result tiles, so it takes their card aspect.
                    categoryRevamp
                        ? VIEW_ALL_ASPECT_CLASSES[searchIndexValue]
                        : 'aspect-tile-default',
                )}
            >
                <Button color="primary" asChild>
                    <Link to={generateURL('/search', urlParams)}>
                        {`View All`}
                        <ArrowRight className="ml-1.5 w-4" />
                    </Link>
                </Button>
            </div>
        );
    };

    const validGroups: SearchResultGroup[] = searchResultGroups.filter(
        (group: SearchResultGroup) => group.tiles.length > 0,
    );

    if (!pageDataFromAtom && !isLoadingFromProps) {
        return <div className="px-6 md:px-0">Loading results...</div>;
    }

    if (isLoadingFromProps) {
        return (
            <div className="space-y-4 px-6 py-8">
                {Array.from({ length: 4 }).map((_, index) => (
                    // Corner on the frame rather than on the skeleton, which
                    // brings its own `rounded-md` — see the note in
                    // `SearchResultPageTilesGrid`.
                    <div
                        key={index}
                        className={
                            categoryRevamp
                                ? 'h-48 w-full overflow-hidden rounded-tile'
                                : 'h-48 w-full'
                        }
                    >
                        <Skeleton className="h-full w-full" variant="light" />
                    </div>
                ))}
            </div>
        );
    }

    return validGroups.length > 0 ? (
        <div className="py-8">
            {validGroups.map((group: SearchResultGroup, groupIndex: number) => {
                return (
                    <div key={groupIndex} className="mb-2">
                        <CarouselSection
                            showArrows={false}
                            size={'mobileSearchResults'}
                            title={
                                SEARCH_CATEGORIES.find(
                                    (c) => c.index === group.index,
                                )?.children
                            }
                        >
                            {group.tiles.map(
                                (tile: TileableModel, tileIndex: number) => (
                                    <CategoryPageTileWithAdminActions
                                        key={`${group.index}-${tileIndex}`}
                                        tile={{
                                            aspectRatio: categoryRevamp
                                                ? getSearchTileAspect(
                                                      tile,
                                                      true,
                                                  )
                                                : 'default',
                                            model: {
                                                ...tile,
                                                ...(isProfessionalTileViewModel(
                                                    tile,
                                                ) && {
                                                    services: [], // Ensure services is an empty array or handle appropriately
                                                }),
                                                // The editorial card, as the
                                                // grid beside it uses. Its
                                                // aspect is `featured`, which
                                                // is `h-full` — `default` has
                                                // no height source inside the
                                                // locked picture box and the
                                                // image would collapse.
                                                ...(isArticleTileViewModel(
                                                    tile,
                                                ) && {
                                                    variant: categoryRevamp
                                                        ? ('editorial' as const)
                                                        : ('default' as const),
                                                }),
                                            },
                                            isFollowed: isInDesignBoard(
                                                tile.id,
                                                getPinType(tile),
                                            ),
                                        }}
                                    />
                                ),
                            )}
                            {generateViewAllTile(group.index)}
                        </CarouselSection>
                        {groupIndex !== validGroups.length - 1 && (
                            <div className="py-4 pl-6 md:pl-0">
                                <Separator />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    ) : (
        <div className="px-6 md:px-0">
            {pageDataFromAtom && (
                <NoMatchesFound
                    hideFilters={true}
                    filterStates={
                        filterStatesFromAtom ?? {
                            visibilityType: 'search',
                            states: {},
                        }
                    }
                    onDisableFilter={props.handleDisableFilter}
                    onResetFilter={props.searchFilters.resetFilters}
                />
            )}
        </div>
    );
};
